import { graphql } from 'gatsby';
import RichTextBlock from '@/components/RichTextBlock/RichTextBlock';
import {
  StyledSection,
  StyledContainer,
  StyledArticle,
  StyledArticleWrapper,
  StyledSidebar,
  StyledComponentWrapper,
  StyledGrid,
  StyledKeyResultsWrapper,
  StyledCTAWrapper,
} from './ArticlePost.styles';
import { type BlockHTMLId } from '@/utils/renderBlock';
import { lazy, Suspense, useContext } from 'react';
import GeneralContext from '@/context/GeneralContext';
const CampaignForm = lazy(() => import('@/components/CampaignForm/CampaignForm'));
const ArticlePostCTA = lazy(() => import('@/components/ArticlePostCTA/ArticlePostCTA'));
const ArticlePostSocialMediaShare = lazy(
  () => import('./components/ArticlePostSocialMediaShare/ArticlePostSocialMediaShare')
);
const ArticlePostKeyResults = lazy(
  () => import('./components/ArticlePostKeyResults/ArticlePostKeyResults')
);
const ArticlePostRelatedContent = lazy(
  () => import('./components/ArticlePostRelatedContent/ArticlePostRelatedContent')
);
const ArticlePostHighlight = lazy(
  () => import('./components/ArticlePostHighlight/ArticlePostHighlight')
);

const ArticlePost = ({
  articlePostElements: elements,
  id,
  system,
}: Queries.ARTICLE_POSTFragment & BlockHTMLId) => {
  const { article_content, sidebar } = elements;
  const richTextData = article_content.value.richTextBlockElements;
  const sidebarItems = sidebar?.value;
  // We filter out social_media_share from the show sidebar calculation,
  // because it's floating absolutely, it's not in the actual content sidebar area
  const showSidebar = Boolean(
    sidebarItems &&
      sidebarItems.filter((item) => item.__typename !== 'kontent_item_social_media_share').length >
        0
  );

  const sidebarArticlePostCTAs = sidebarItems?.filter(
    (item) => item.__typename === 'kontent_item_article_post_cta'
  );
  const hasCTAs = sidebarArticlePostCTAs && sidebarArticlePostCTAs.length > 0;

  const relatedContentItems =
    sidebarItems
      ?.filter((item) => item.__typename === 'kontent_item_article_post_related_content')
      .map((item) => (
        <Suspense key={item.system.id} fallback={null}>
          <ArticlePostRelatedContent {...item} />
        </Suspense>
      )) ?? [];
  const hasRelatedContent = relatedContentItems.length > 0;

  const campaignFormItems =
    sidebarItems
      ?.filter((item) => item.__typename === 'kontent_item_campaign_form')
      .map((item) => (
        <Suspense key={item.campaignFormSystem.id} fallback={null}>
          <CampaignForm {...item} />
        </Suspense>
      )) ?? [];
  const hasCampaignForm = campaignFormItems.length > 0;

  const hasSocialMediaShare = sidebarItems?.some(
    (item) => item.__typename === 'kontent_item_social_media_share'
  );

  const keyResultsAndHighlightItems =
    sidebarItems
      ?.map((item) => {
        switch (item.__typename) {
          case 'kontent_item_article_post_key_results':
            return (
              <Suspense fallback={null}>
                <ArticlePostKeyResults {...item} />
              </Suspense>
            );
          case 'kontent_item_article_post_highlight':
            return (
              <Suspense fallback={null}>
                <ArticlePostHighlight {...item} />
              </Suspense>
            );
          default:
            return null;
        }
      })
      ?.filter((item) => item !== null) ?? [];
  const hasKeyResultsAndHighlight = keyResultsAndHighlightItems?.length > 0;
  const { isInformationStripeVisible } = useContext(GeneralContext);

  return (
    <>
      <StyledSection id={id} data-codename={system.codename}>
        <StyledContainer>
          <StyledGrid showSidebar={showSidebar}>
            <StyledArticle showSidebar={showSidebar}>
              {hasKeyResultsAndHighlight && (
                <StyledComponentWrapper hideOnDesktop>
                  <StyledKeyResultsWrapper>
                    <Suspense fallback={null}>{keyResultsAndHighlightItems}</Suspense>
                  </StyledKeyResultsWrapper>
                </StyledComponentWrapper>
              )}
              <StyledComponentWrapper hideOnDesktop>
                <StyledCTAWrapper>
                  {sidebarArticlePostCTAs?.map((item) => (
                    <ArticlePostCTA key={item.system.id} {...item} />
                  ))}
                </StyledCTAWrapper>
              </StyledComponentWrapper>
              <StyledArticleWrapper>
                <RichTextBlock elements={richTextData} />
              </StyledArticleWrapper>
            </StyledArticle>
            {showSidebar && (
              <StyledSidebar
                data-testid="article-post-sidebar"
                isInformationStripeVisible={isInformationStripeVisible}
              >
                {hasKeyResultsAndHighlight && (
                  <StyledComponentWrapper showOnDesktop>
                    <StyledKeyResultsWrapper>
                      <Suspense fallback={null}>{keyResultsAndHighlightItems}</Suspense>
                    </StyledKeyResultsWrapper>
                  </StyledComponentWrapper>
                )}
                {hasCTAs && (
                  <StyledComponentWrapper showOnDesktop>
                    <StyledCTAWrapper>
                      {sidebarArticlePostCTAs?.map((item) => (
                        <ArticlePostCTA key={item.system.id} {...item} />
                      ))}
                    </StyledCTAWrapper>
                  </StyledComponentWrapper>
                )}
                {hasCampaignForm && (
                  <StyledComponentWrapper>{campaignFormItems}</StyledComponentWrapper>
                )}
                {hasRelatedContent && (
                  <StyledComponentWrapper>{relatedContentItems}</StyledComponentWrapper>
                )}
              </StyledSidebar>
            )}
          </StyledGrid>
        </StyledContainer>
      </StyledSection>
      {hasSocialMediaShare && (
        <StyledComponentWrapper>
          <Suspense fallback={null}>
            <ArticlePostSocialMediaShare />
          </Suspense>
        </StyledComponentWrapper>
      )}
    </>
  );
};

export default ArticlePost;

export const fragment = graphql`
  fragment ARTICLE_POST on kontent_item_article_post {
    system {
      codename
    }
    articlePostElements: elements {
      ...COMPONENT_A_B_TEST
      article_content {
        value {
          ... on kontent_item_rich_text_block {
            ...RICH_TEXT_BLOCK
          }
        }
      }
      sidebar {
        value {
          __typename
          ...ARTICLE_POST_RELATED_CONTENT
          ...ARTICLE_POST_CTA
          ...ARTICLE_POST_KEY_RESULTS
          ...ARTICLE_POST_HIGHLIGHT
          ...CAMPAIGN_FORM
          ...SOCIAL_MEDIA_SHARE
        }
      }
    }
  }
`;
